import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { MergedRoute } from '@next/core-lib/router-store';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  filter,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { getTenantName } from 'src/app/branding/store/branding.selectors';

import { UpsertBarnResponse } from '../../models/barn-service.model';
import { Barn, BarnModuleState, Silo } from '../../models/barn.model';
import { BarnService } from '../../services/barn.service';
import { SiloService } from '../../services/silo.service';
import * as barnActions from '../actions/barn.actions';
import { getSelectedBarn, getSelectedBarnId } from '../selectors/barn.selector';

@Injectable()
export class BarnEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BarnModuleState>,
    private router: Router,
    private ngZone: NgZone,
    private barnService: BarnService,
    private siloService: SiloService,
    private dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
  ) {}

  loadSilos$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.loadSilosRequest),
      withLatestFrom(this.store.select(getTenantName)),
      filter(([_, tenantName]) => tenantName == 'Agrifirm'),
      switchMap(([_]) =>
        this.store.pipe(
          select(UserSelectors.selectLocation),
          skipWhile((location) => location === null || location === undefined),
          take(1),
        ),
      ),
      mergeMap(() =>
        this.siloService.getSilos().pipe(
          switchMap((silos: Silo[]) => of(barnActions.loadSilosSuccess({ silos }))),
          catchError((error: HttpErrorResponse) =>
            of(
              barnActions.loadSilosFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadBarns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.loadBarnsRequest),
      mergeMap(() =>
        this.barnService.getBarns().pipe(
          withLatestFrom(this.store.select(getSelectedBarnId)),
          switchMap(([barns, barnId]: [Barn[], number]) =>
            of(barnActions.loadBarnsSuccess({ barns }), barnActions.setSelectedBarn({ barnId })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              barnActions.loadBarnsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.deleteBarnRequest),
      mergeMap(({ barn }) =>
        this.barnService.deleteBarn(barn.id).pipe(
          map(() => barnActions.deleteBarnSuccess({ barn })),
          catchError((error: HttpErrorResponse) =>
            of(
              barnActions.deleteBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveSelectedBarn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.upsertBarn),
      switchMap((_) => this.store.pipe(select(getSelectedBarn), take(1))),
      map((barn: Barn) => barnActions.upsertBarnRequest({ barn })),
    ),
  );

  upsertBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.upsertBarnRequest),
      exhaustMap(({ barn }) =>
        this.barnService.upsertBarn(barn).pipe(
          map((response: UpsertBarnResponse) =>
            barnActions.upsertBarnSuccess({
              barn: {
                ...barn,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              barnActions.upsertBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  changeNavigationUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction<MergedRoute>) =>
          action.payload.routerState.params['barnId'],
      ),
      map((barnId) => barnActions.setSelectedBarn({ barnId: parseInt(barnId, 10) })),
    ),
  );

  upsertBarnSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(barnActions.upsertBarnSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'barn'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteBarnWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(barnActions.deleteBarnWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('barn-item')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                barnActions.deleteBarnRequest({
                  barn: action.barn,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string): object {
    return {
      text: this.locale.translate(`settings.${type}.delete-item`),
    };
  }
}
